import React from 'react';
import Footer from '../../../../components/Footer';
import BtechImg from "../../../../assets/images/engineering.jpeg";
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Beaker,
    GraduationCap,
    Building2,
    Target,
    BookOpen,
 
    FlaskConical,
    MapPin,
    Users,
    Workflow
} from 'lucide-react';

const BtechCivil = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    return (
        <div className='w-full relative font-poppins'>
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className='relative h-[300px] md:h-[400px] bg-[#0e4d38] mt-[60px] md:mt-[80px] overflow-hidden'
            >
                <div className='absolute inset-0 bg-black/50'></div>
                <div className='relative z-10 h-full flex flex-col justify-center items-center text-white px-4'>
                    <h1 className='text-3xl md:text-6xl font-bold text-center mb-4'>
                        Bachelor of Technology in Civil Engineering
                    </h1>
                    <p className='text-lg md:text-xl text-center max-w-3xl'>
                        Building the Future, Engineering Tomorrow's Infrastructure
                    </p>
                </div>
            </motion.div>

            <div className='w-11/12 md:w-10/12 mx-auto mt-8 md:mt-16'>
                {/* About Section */}
                <motion.div
                    {...fadeIn}
                    className='flex flex-col md:flex-row gap-8 mb-10 md:gap-[80px]'
                >
                    <img src={BtechImg} className='md:w-[40%] rounded-xl shadow-lg' alt="Civil Engineering" />
                    <div className='flex flex-col gap-4'>
                        <div className='flex items-center gap-3'>
                            <div className='p-2 rounded-lg bg-[#0e4d38] text-white'>
                                <Building2 size={24} />
                            </div>
                            <h4 className='font-bold text-2xl md:text-3xl text-[#0e4d38]'>Program Overview </h4>
                        </div>
                        <div className='text-justify md:text-lg space-y-4 text-gray-700'>
                            The importance of construction projects in the context of sustainable development has been steadily increasing, leading to a growing demand for civil engineering professionals. The Faculty of Engineering offers a four-year degree program in Civil Engineering, aiming to equip students with the knowledge and skills necessary for construction and building design. The Civil Engineering Department boasts a team of expert faculty members and resources from prestigious institutions, committed to providing students with both a solid theoretical foundation and practical engineering experience. To further enhance practical skills, the department is equipped with state-of-the-art laboratories and research centers specializing in various subfields of Civil Engineering, including Structural Engineering, Earthquake Engineering, Transportation Engineering, Geotechnical Engineering, Fluid Mechanics, Environmental Engineering, and Concrete Technology. In addition to academic excellence, the department focuses on preparing students for the professional world, helping them develop the career competencies necessary to succeed in the industry.
                        </div>
                        <Link to="https://admission.maya.edu.in/">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className='mt-5 md:mt-4 bg-[#23376c] px-6 py-3 text-white rounded-full font-semibold transition-all duration-200 hover:bg-[#4865b3] flex items-center gap-2'
                            >
                                <GraduationCap size={20} />
                                Apply Now
                            </motion.button>
                        </Link>
                    </div>
                </motion.div>

                {/* Learning Outcomes */}
                <motion.div {...fadeIn} className='mb-12 bg-white p-6 rounded-xl shadow-lg'>
                    <div className='flex items-center gap-3 mb-6'>
                        <div className='p-2 rounded-lg bg-[#0e4d38] text-white'>
                            <Target size={24} />
                        </div>
                        <h4 className='font-bold text-2xl text-[#0e4d38]'>Learning Outcomes</h4>
                    </div>
                    <p className='text-gray-700 leading-relaxed mb-4'>
                        Civil Engineering plays a crucial role in the development of infrastructure, whether for public or private use. As a result, civil engineers hold a position of great importance, and the field offers a wide range of career opportunities for graduates. The program develops engineers who have the ability to navigate the various streams in civil engineering, which at the same time develops their industrial skills in areas such as:
                    </p>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                        {[
                            "•	Site Engineer",
                            "•	Design Engineer",
                            "•	Construction Planner",
                            "•	Project Manager",
                            "•	Surveyor",
                            "•	Urban Planner"
                        ].map((role, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ scale: 1.02 }}
                                className='p-4 bg-gray-50 rounded-lg border border-gray-200'
                            >
                                <p className='text-gray-700 font-medium'>{role}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Laboratories and Facilities  */}

                <motion.div {...fadeIn} className='mb-12 bg-white p-6 rounded-xl shadow-lg'>
                    <div className='flex items-center gap-3 mb-6'>
                        <div className='p-2 rounded-lg bg-[#0e4d38] text-white'>
                            <FlaskConical size={24} />
                        </div>
                        <h4 className='font-bold text-2xl text-[#0e4d38]'>Laboratories and Facilities</h4>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                        {[
                            "•	Fluid Mechanics Lab ",
                            "•	Surveying Lab ",
                            "•	Environmental Engineering Lab ",
                            "•	Geotechnical Engineering Lab ",
                            "•	Concrete Technology Lab ",
                            "•	Structural Engineering Lab "
                        ].map((role, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ scale: 1.02 }}
                                className='p-4 bg-gray-50 rounded-lg border border-gray-200'
                            >
                                <p className='text-gray-700 font-medium'>{role}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>


                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-white p-6 rounded-xl shadow-lg mb-12"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-lg bg-[#0e4d38] text-white">
                            <GraduationCap size={24} />
                        </div>
                        <h4 className="font-bold text-2xl text-[#0e4d38]">Admission Requirements</h4>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                            <div className="w-2 h-2 bg-[#23376c] rounded-full"></div>
                            <p className="text-gray-700">
                                <span className="font-semibold">Duration of the Program:</span> 4 Years (Semester System)
                            </p>
                        </div>

                        <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                            <div className="w-2 h-2 bg-[#23376c] rounded-full"></div>
                            <p className="text-gray-700">
                                <span className="font-semibold">Affiliation:</span> Constituent College of Maya Devi University
                            </p>
                        </div>

                        <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                            <div className="w-2 h-2 bg-[#23376c] rounded-full mt-2"></div>
                            <p className="text-gray-700">
                                <span className="font-semibold">Eligibility Criteria:</span> Candidate shall have Passed 12th Examination with Physics, Chemistry, Maths/Biology subjects and in these subjects along with theory or theory + practical candidate should have obtained at least 45% in case of general category and 40% in case of reserved category from recognized board.
                            </p>
                        </div>

                        <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                            <div className="w-2 h-2 bg-[#23376c] rounded-full"></div>
                            <p className="text-gray-700">
                                <span className="font-semibold">Mode of Admission:</span> As per university procedure
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
            <Footer />
        </div>
    );
};

export default BtechCivil;